import { forwardRef, FunctionComponent, ReactNode, SVGProps, useState, useEffect, useRef } from 'react'
import { useButton } from '@react-aria/button'
import { ComboBoxStateOptions, useComboBoxState } from '@react-stately/combobox'
import { AriaComboBoxProps, useComboBox } from '@react-aria/combobox'
import { useFilter } from '@react-aria/i18n'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import cx from 'classnames'
import Styles from './ComboBox.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { InputProps } from 'almanac-ui/TextInputField/Types'
import { useObjectRef } from '@react-aria/utils'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { autoUpdate, flip, hide, inline, offset, shift, size, useFloating } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { Popover } from 'components/Popover/Popover'
import { ChevronDown } from 'blaze-ui/icons'
import { Options } from '@floating-ui/dom/src/autoUpdate'
import { useListBoxLayout } from 'components/ListBox/useListBoxLayout'
import { ComboBoxProvider } from './ComboBoxContext'
import { CSSProperties } from 'react'

export interface ComboBoxProps
  extends AriaComboBoxProps<any>,
    Pick<InputProps, 'size' | 'disabled'>,
    Pick<ComboBoxStateOptions<any>, 'defaultFilter'> {
  prefixIcon?: FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>
  postfixIcon?: FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>
  'aria-label'?: string
  optionClassName?: string
  wrapperClassName?: string
  popoverClassName?: string
  contentsClassName?: string
  inputClassName?: string
  inputStyle?: CSSProperties
  focusOnIconClick?: boolean
  noResultsMessage?: ReactNode
  size?: InputProps['size']
  popoverMaxHeight?: number
  isValid?: boolean
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  autoUpdateOptions?: Partial<Options>
  popoverRef?: (el: HTMLDivElement) => void
  errorMessage?: ReactNode
  chevronSize?: number
  isVirtualized?: boolean
  matchReferenceWidth?: boolean
  __UNSAFE_popoverStyle?: CSSProperties
}

export const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>(
  (
    {
      prefixIcon: PrefixIcon,
      postfixIcon: PostfixIcon,
      size: inputSize = 'md',
      optionClassName,
      wrapperClassName,
      popoverClassName,
      contentsClassName,
      inputClassName,
      inputStyle,
      focusOnIconClick = false,
      noResultsMessage,
      popoverMaxHeight,
      errorMessage,
      isValid,
      disabled,
      autoUpdateOptions,
      chevronSize = 12,
      isVirtualized = true,
      matchReferenceWidth = false,
      __UNSAFE_popoverStyle,
      ...props
    },
    fRef,
  ) => {
    // Setup filter function and state.
    const { contains } = useFilter({ sensitivity: 'base' })
    const state = useComboBoxState({ defaultFilter: contains, allowsEmptyCollection: !!noResultsMessage, ...props })

    // Setup refs and get props for child elements.
    const buttonRef = useObjectRef<HTMLButtonElement>(null)
    const inputRef = useObjectRef<HTMLInputElement>(fRef)
    const containerRef = useRef<HTMLDivElement>(null)
    const listBoxRef = useRef<HTMLDivElement>(null)
    const [popoverRef, setPopoverRef] = useState<HTMLDivElement>(document.createElement('div'))

    const {
      buttonProps: triggerProps,
      inputProps,
      listBoxProps,
      labelProps,
      validationDetails,
      validationErrors,
    } = useComboBox(
      {
        ...props,
        inputRef,
        buttonRef,
        listBoxRef: { current: popoverRef },
        popoverRef: { current: popoverRef },
      },
      state,
    )
    const { buttonProps } = useButton(triggerProps, buttonRef)

    const renderIcon = (
      Icon: FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>,
      className?: string,
    ) => (
      <Icon
        className={cx(className, { [Styles.pointer]: focusOnIconClick })}
        onClick={() => focusOnIconClick && inputRef.current?.focus()}
        aria-hidden
      />
    )

    const { isOpen, close } = useOverlayTriggerState({ isOpen: state.isOpen })
    const { x, y, refs, middlewareData } = useFloating({
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [
        offset({ mainAxis: 4, crossAxis: !!PrefixIcon ? -28 : 0 }),
        inline(),
        flip(),
        shift(),
        hide({ strategy: 'referenceHidden', boundary: 'clippingAncestors' }),
        size({
          boundary: 'clippingAncestors',
          apply({ availableHeight, elements, rects }) {
            Object.assign(elements.floating.style, {
              maxHeight: `${popoverMaxHeight ?? availableHeight}px`,
              ...(matchReferenceWidth && { width: `${rects.reference.width}px` }),
            })
          },
        }),
      ],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
          ...autoUpdateOptions,
        })
      },
    })

    useEffect(() => {
      // Use the container as reference when matching width, otherwise use input
      const referenceElement = matchReferenceWidth ? containerRef.current : inputRef.current
      refs.setReference(referenceElement)
    }, [refs, inputRef, containerRef, matchReferenceWidth])

    useEffect(() => {
      refs.setFloating(null)
    }, [refs, isOpen])

    const isVisible = !middlewareData.hide?.referenceHidden && x !== null && y !== null

    const layout = useListBoxLayout(state, false)

    const listBoxBaseProps = {
      ref: listBoxRef,
      state: state,
      optionClassName: optionClassName,
    }

    return (
      <div className={wrapperClassName} onPointerEnter={props.onPointerEnter} onPointerLeave={props.onPointerLeave}>
        {props.label && <label {...labelProps}>{props.label}</label>}
        <div ref={containerRef} className={cx(Styles.comboBoxContents, contentsClassName)}>
          {PrefixIcon && <div className={Styles.prefixIconContainer}>{renderIcon(PrefixIcon, Styles.prefixIcon)}</div>}
          <Input
            className={cx(Styles.input, inputClassName, {
              [Styles.withPrefixIcon]: PrefixIcon,
              [Styles.withPostfixIcon]: PostfixIcon,
            })}
            withBorder={false}
            name="combobox"
            type="text"
            ref={inputRef}
            {...inputProps}
            value={inputProps.value as string}
            size={inputSize}
            errorMessage={errorMessage}
            isValid={isValid}
            disabled={disabled}
            validationDetails={validationDetails}
            validationErrors={validationErrors}
            {...(inputStyle && { style: inputStyle })}
          />

          {PostfixIcon && renderIcon(PostfixIcon, Styles.postfixIcon)}
          <button {...buttonProps} ref={buttonRef}>
            <div className={Styles.chevron} aria-hidden="true">
              <ChevronDown size={chevronSize} />
            </div>
          </button>
          {isOpen && (
            <FloatingPortal id="floating-ui-root">
              <Popover
                style={{
                  position: 'fixed',
                  visibility: isVisible ? 'visible' : 'hidden',
                  top: y || 0,
                  left: x || 0,
                  ...(popoverMaxHeight && { maxHeight: `${popoverMaxHeight}px` }),
                  ...__UNSAFE_popoverStyle,
                }}
                isOpen={isOpen}
                onClose={close}
                ref={(el) => {
                  if (el) {
                    setPopoverRef(el)
                    refs.setFloating(el)
                    props.popoverRef?.(el)
                  }
                }}
                containFocus={false}
                restoreFocus
                noOverlay
                autoFocus={false}
                className={popoverClassName}
              >
                <ComboBoxProvider close={state.close}>
                  {state.collection.size === 0 && noResultsMessage ? (
                    noResultsMessage
                  ) : (
                    <>
                      {isVirtualized ? (
                        <ListBoxBase {...listBoxProps} {...listBoxBaseProps} layout={layout} isVirtualized />
                      ) : (
                        <ListBoxBase {...listBoxProps} {...listBoxBaseProps} isVirtualized={false} />
                      )}
                    </>
                  )}
                </ComboBoxProvider>
              </Popover>
            </FloatingPortal>
          )}
        </div>
      </div>
    )
  },
)
